export interface UserData {
  user?: string;
  userId?: string;
  email?: string;
  name?: string;
  givenName?: string;
  familyName?: string;
  shortId?: string;
  [key: string]: any;
}

export type MainDisplayFields =
  | 'fullName'           // givenName familyName
  | 'fullNameReversed'   // familyName, givenName
  | 'givenName'          // givenName only
  | 'familyName'         // familyName only
  | 'email'              // email only
  | 'userId'             // userId only
  | 'name'               // name (shortId) only
  | 'custom';            // custom format

export type ParenthesesContent =
  | 'none'               // no parentheses
  | 'name'               // (shortId)
  | 'userId'             // (userId)
  | 'email'              // (email)
  | 'shortId'            // (shortId)
  | 'givenName'          // (givenName)
  | 'familyName'         // (familyName)
  | 'initials'           // (AB)
  | 'custom';            // custom content

export interface UserFormatConfig {
  /** Main display format */
  main?: MainDisplayFields;

  /** Content to show in parentheses */
  parentheses?: ParenthesesContent;

  /** Custom main format function */
  customMain?: (user: UserData) => string | null;

  /** Custom parentheses content function */
  customParentheses?: (user: UserData) => string | null;

  /** Separator between first and last name */
  nameSeparator?: string;

  /** What to use as fallback if primary fields are missing */
  fallback?: 'email' | 'userId' | 'name' | 'unknown' | ((user: UserData) => string);

  /** Text to show when user data is completely missing */
  emptyText?: string;

  /** Whether to trim whitespace */
  trim?: boolean;

  /** Whether to handle null/undefined gracefully */
  safe?: boolean;
}

export const USER_FORMAT_PRESETS = {
  /** Default format: "FirstName LastName (shortId)" with email fallback */
  default: {
    main: 'fullName',
    parentheses: 'name',
    fallback: 'email',
    emptyText: 'Unknown User'
  } as UserFormatConfig,

  /** Full name only: "FirstName LastName" with email fallback */
  nameOnly: {
    main: 'fullName',
    parentheses: 'none',
    fallback: 'email'
  } as UserFormatConfig,

  /** With user ID: "FirstName LastName (userId)" */
  withUserId: {
    main: 'fullName',
    parentheses: 'userId',
    fallback: 'email'
  } as UserFormatConfig,

  /** With email: "FirstName LastName (email)" */
  withEmail: {
    main: 'fullName',
    parentheses: 'email',
    fallback: 'userId'
  } as UserFormatConfig,

  /** Reversed: "LastName, FirstName" */
  reversed: {
    main: 'fullNameReversed',
    parentheses: 'none',
    fallback: 'email'
  } as UserFormatConfig,

  /** Email only */
  emailOnly: {
    main: 'email',
    parentheses: 'none',
    fallback: 'userId'
  } as UserFormatConfig,

  /** Compact: "FirstName LastName (AB)" where AB are initials */
  compact: {
    main: 'fullName',
    parentheses: 'initials',
    fallback: 'email'
  } as UserFormatConfig
};

export function getBookedUserDisplay(user: UserData | null | undefined): string {
  if (!user) return '-';

  const fullName = [user.givenName, user.familyName].filter(Boolean).join(' ').trim();
  if (fullName && user.name) return `${fullName} (${user.name})`;

  return fullName || user.name || user.email || user.user || user.userId || '-';
}

export function getReservedForUsers(booking: {
  reservedForUsers?: string[] | null;
} | null | undefined): string[] {
  return (booking?.reservedForUsers ?? []).filter(Boolean);
}

export function getReservedForBookedUsers(booking: {
  reservedForUsers?: string[] | null;
  reservedForEmails?: string[] | null;
} | null | undefined, users: UserData[] = []): UserData[] {
  const reservedForEmails = booking?.reservedForEmails ?? [];
  return getReservedForUsers(booking).map((user, index) => ({
    user,
    email: reservedForEmails[index],
    ...users.find((candidate) => candidate?.userId === user || candidate?.user === user),
  }));
}

export function getBookingForUsers(booking: {
  bookedUsers?: UserData[] | null;
  reservedForEmails?: string[] | null;
  reservedForUsers?: string[] | null;
} | null | undefined): UserData[] {
  const bookedUsers = (booking?.bookedUsers ?? []).filter(Boolean);
  if (bookedUsers.length) return bookedUsers;

  const reservedForEmails = (booking?.reservedForEmails ?? []).filter(Boolean);
  if (reservedForEmails.length) return reservedForEmails.map((email) => ({ email }));

  return (booking?.reservedForUsers ?? []).filter(Boolean).map((user) => ({ user }));
}

/**
 * Formats user data according to the specified configuration
 */
export function formatUserDisplay(
  user: UserData | null | undefined,
  config: UserFormatConfig = USER_FORMAT_PRESETS.default
): string {
  // Fallback to 'Unknown User' if user is null/undefined
  if (!user) {
    return config.emptyText || 'Unknown User';
  }

  const {
    main = 'fullName',
    parentheses = 'none',
    customMain,
    customParentheses,
    nameSeparator = ' ',
    fallback = 'email',
    emptyText = 'Unknown User',
    trim = true,
    safe = true
  } = config;

  const get = (key: string): string => {
    const value = user[key];
    if (safe && (value === null || value === undefined)) {
      return '';
    }
    return String(value || '');
  };

  let mainDisplay: string | null = null;

  if (customMain) {
    mainDisplay = customMain(user);
  } else {
    switch (main) {
      case 'fullName':
        if (user.givenName && user.familyName) {
          mainDisplay = `${get('givenName')}${nameSeparator}${get('familyName')}`;
        }
        break;

      case 'fullNameReversed':
        if (user.familyName && user.givenName) {
          mainDisplay = `${get('familyName')}, ${get('givenName')}`;
        }
        break;

      case 'givenName':
        mainDisplay = get('givenName') || null;
        break;

      case 'familyName':
        mainDisplay = get('familyName') || null;
        break;

      case 'email':
        mainDisplay = get('email') || null;
        break;

      case 'userId':
        mainDisplay = get('userId') || null;
        break;

      case 'name':
        mainDisplay = get('name') || get('shortId') || null;
        break;
    }
  }

  let parenContent: string | null = null;

  if (customParentheses) {
    parenContent = customParentheses(user);
  } else if (parentheses !== 'none') {
    switch (parentheses) {
      case 'name':
        parenContent = get('name') || get('shortId') || null;
        break;

      case 'userId':
        parenContent = get('userId') || null;
        break;

      case 'email':
        parenContent = get('email') || null;
        break;

      case 'shortId':
        parenContent = get('shortId') || get('name') || null;
        break;

      case 'givenName':
        parenContent = get('givenName') || null;
        break;

      case 'familyName':
        parenContent = get('familyName') || null;
        break;

      case 'initials':
        const g = get('givenName');
        const f = get('familyName');
        if (g && f) {
          parenContent = `${g.charAt(0).toUpperCase()}${f.charAt(0).toUpperCase()}`;
        }
        break;
    }
  }

  if (!mainDisplay || mainDisplay.trim() === '') {
    if (typeof fallback === 'function') {
      mainDisplay = fallback(user);
    } else {
      switch (fallback) {
        case 'email':
          mainDisplay = get('email');
          break;
        case 'userId':
          mainDisplay = get('userId');
          break;
        case 'name':
          mainDisplay = get('name') || get('shortId');
          break;
        case 'unknown':
        default:
          mainDisplay = emptyText;
          break;
      }
    }
  }

  let result = mainDisplay || emptyText;

  if (parenContent && parenContent.trim() !== '') {
    result = `${result} (${parenContent})`;
  }

  return trim ? result.trim() : result;
}

/**
 * Utility function to create custom format configurations
 */
export function createUserFormat(config: Partial<UserFormatConfig>): UserFormatConfig {
  return {
    ...USER_FORMAT_PRESETS.default,
    ...config
  };
}

/**
 * Format multiple users with the same configuration
 */
export function formatUsersList(
  users: (UserData | null | undefined)[],
  config: UserFormatConfig = USER_FORMAT_PRESETS.default
): string[] {
  return users.map(user => formatUserDisplay(user, config));
}

/**
 * Get initials from user data
 */
export function getUserInitials(user: UserData | null | undefined): string {
  if (!user) return '';

  const givenInitial = user.givenName?.charAt(0)?.toUpperCase() || '';
  const familyInitial = user.familyName?.charAt(0)?.toUpperCase() || '';

  return givenInitial + familyInitial;
}

/**
 * Extract initals from an email adress or full name string
 *
 * For emails: Splits on @ and extracts initals from the local part
 * (splits on . _ -)
 * For names: Splits on whitespace and takes first character of first two
 * parts
 *
 * @param value - Email address (user.name@domain.com) or full name
 * string ("Joe Doe")
 * @returns Two-character uppercase initals, or "??" if input is empty
 */
export const getUserInitalsFromString = (value?: string): string => {
  if (!value) return "??";

  if (value.includes("@")) {
    const namePart = value.split("@")[0];
    const parts = namePart.split(/[._-]/);
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    }
    return namePart.substring(0, 2).toUpperCase();
  }

  const nameParts = value.trim().split(/\s+/);
  if (nameParts.length >= 2) {
    return (nameParts[0].charAt(0) + nameParts[1].charAt(0)).toUpperCase();
  }

  return value.substring(0, 2).toUpperCase();
};


/**
 * Extract and format name from an email address
 *
 * Splits email on @ and extracts first name and last name from the local part,
 * then splits on . and formats the name
 *
 * @param email - Email address (user.name@domain.com)
 * @returns Full name string ("John Doe")
 */
export const getNameFromEmail = (email?: string): string => {
  const namePart = email?.split("@");

  if (!namePart) return '-';

  const firstNameLastName = namePart[0]?.split(".");
  const firstName = firstNameLastName[0] ? firstNameLastName[0].charAt(0).toUpperCase() + firstNameLastName[0].slice(1) : '';
  const lastName = firstNameLastName[1] ? firstNameLastName[1].charAt(0).toUpperCase() + firstNameLastName[1].slice(1) : '';
  return lastName ? `${firstName} ${lastName}` : firstName;
}

/**
  * Find user by ID in a list and return formatted display name
  *
  * @param userId - User ID to search for
  * @param usersList - Array of user data objects
  * @param fallback - Value to return if user not found (defaults to
  userId)
  * @returns Formatted user display or fallback value
*/
export const getUserDisplayById = (
  userId?: string,
  usersList?: UserData[],
  fallback?: string
): string => {
  if (!userId || !usersList) return userId || '';

  const user = usersList.find(user => user.userId === userId);

  return formatUserDisplay(user, USER_FORMAT_PRESETS.nameOnly) || fallback || userId;
}

/**
 * Check if user has complete name information
 */
export function hasCompleteName(user: UserData | null | undefined): boolean {
  return !!(user?.givenName && user?.familyName);
}

/**
 * Get display name with smart fallback
 * Tries: Full name -> Email -> UserId -> "Unknown"
 */
export function getDisplayName(user: UserData | null | undefined): string {
  if (!user) return 'Unknown User';

  if (user.givenName && user.familyName) {
    return `${user.givenName} ${user.familyName}`;
  }

  return user.email || user.userId || user.name || 'Unknown User';
}
